import { FC } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { styled } from '@mui/material';
import { Column } from 'further-ui/components/Layout';
import {
  ResponseSegmentType,
  NavigationRoute,
} from 'further-types/lp/ai-agent';
import FileUploadTaskPrompt from './FileUploadTaskPrompt';
import IncomeExpenseTaskPrompt from './IncomeExpenseTaskPrompt';
import NavigationTaskPrompt from './NavigationTaskPrompt';
import ToolCallIndicator from './ToolCallIndicator';
import { ChatMessageAgent, PendingInvoice } from '../../types';
import Timestamp from '../Timestamp';

const AiText = styled('div')(({ theme }) => ({
  ...theme.typography.body2,
  wordBreak: 'break-word',
  lineHeight: 1.65,
  paddingLeft: theme.spacing(2),
  color: theme.palette.grey[900],
  '& p': { margin: 0 },
  '& p + p': { marginTop: theme.spacing(3) },
  '& ul, & ol': {
    paddingLeft: theme.spacing(6),
    margin: `${theme.spacing(3)} 0`,
  },
  '& li ul, & li ol': { margin: `${theme.spacing(2)} 0` },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    margin: `${theme.spacing(3)} 0`,
  },
  '& th, & td': {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5, 2),
    textAlign: 'left',
  },
  '& th': { backgroundColor: theme.palette.grey[50], fontWeight: 600 },
}));

const TaskPromptWrapper = styled('div')``;

const ALLOWED_MARKDOWN_ELEMENTS = [
  'p',
  'strong',
  'em',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'a',
];

const MARKDOWN_COMPONENTS = {
  a: ({ href, children }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a
      href={href}
      target={href?.startsWith('mailto:') ? undefined : '_blank'}
      rel="noopener noreferrer"
      style={{ color: 'inherit' }}
    >
      {children}
    </a>
  ),
};

const isIncomeExpenseNav = (
  route: NavigationRoute,
  pendingInvoice: PendingInvoice | null,
) =>
  pendingInvoice !== null &&
  (route === NavigationRoute.IncomeAdd || route === NavigationRoute.ExpenseAdd);

type Props = {
  message: ChatMessageAgent;
  onAttachClick: () => void;
};

const AgentResponse: FC<Props> = ({ message, onAttachClick }) => {
  return (
    <Column spacing="sm">
      {message.segments.map((seg, segIndex) => {
        if (seg.type === ResponseSegmentType.Text) {
          return (
            <AiText key={segIndex}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                allowedElements={ALLOWED_MARKDOWN_ELEMENTS}
                unwrapDisallowed
                components={MARKDOWN_COMPONENTS}
              >
                {seg.content}
              </ReactMarkdown>
            </AiText>
          );
        }

        if (seg.type === ResponseSegmentType.FileUpload) {
          return (
            <TaskPromptWrapper key={segIndex}>
              <FileUploadTaskPrompt
                label={seg.label}
                onAttachClick={onAttachClick}
              />
            </TaskPromptWrapper>
          );
        }

        if (seg.type === ResponseSegmentType.ToolCall) {
          return <ToolCallIndicator key={segIndex} label={seg.label} />;
        }

        if (seg.type === ResponseSegmentType.Navigation) {
          return isIncomeExpenseNav(seg.route, message.pendingInvoice) ? (
            <TaskPromptWrapper key={segIndex}>
              <IncomeExpenseTaskPrompt
                navigation={seg}
                pendingInvoice={message.pendingInvoice}
              />
            </TaskPromptWrapper>
          ) : (
            <TaskPromptWrapper key={segIndex}>
              <NavigationTaskPrompt navigation={seg} />
            </TaskPromptWrapper>
          );
        }
      })}
      <Timestamp date={message.timestamp} />
    </Column>
  );
};

export default AgentResponse;
